"use client";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { memo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { IndustryOfferProps } from "./types";
import { Typography } from "@/components/ui/typography";

const IndustryOffer = ({ content, className }: IndustryOfferProps) => {
  const { offerTitle, offerImage, offerItems } = content;
  const [activeAccordion, setActiveAccordion] = useState<string>("item-0");

  if (!offerTitle && !offerImage && !offerItems?.length) {
    return null;
  }

  const imageUrl = offerImage?.fields?.file?.url
    ? `https:${offerImage.fields.file.url}`
    : null;

  const imageDimensions = {
    width: offerImage?.fields?.file?.details?.image?.width ?? 449,
    height: offerImage?.fields?.file?.details?.image?.height ?? 300,
  };

  return (
    <section
      className={cn("section bg-background-secondary lg:bg-white py-12 lg:pt-0 lg:pb-20", className)}
      itemScope
      itemType="https://schema.org/ItemList"
    >
      <div className="container mx-auto">
        <div className="grid grid-cols-1 gap-6 place-content-start lg:gap-12">
          <div className="grid grid-cols-1 gap-4 place-content-start md:gap-8 text-center">
            {!!offerTitle && (
              <Typography
                variant="heading4"
                className="lg:text-[32px] lg:leading-[150%] lg:text-grey-800"
              >
                <h2 itemProp="name">{offerTitle}</h2>
              </Typography>
            )}
          </div>
          <div className="grid grid-cols-1 gap-6 place-content-start md:grid-cols-2 md:gap-6 lg:grid-cols-[minmax(0,0.4189fr)_minmax(0,0.5811fr)] lg:gap-[min(3rem,3.33vw)]">
            {!!imageUrl && (
              <div className="flex-auto min-w-0 rounded-2xl overflow-hidden inline-flex md:items-start aspect-1 sm:aspect-auto">
                <Image
                  src={imageUrl}
                  width={imageDimensions?.width ?? 449}
                  height={imageDimensions?.height ?? 300}
                  alt={offerTitle ?? "Laguex illumination"}
                  className="flex-auto min-w-0 object-top object-cover"
                />
              </div>
            )}
            <div className="grid grid-cols-1 place-content-start gap-4 text-semantic-text-primary">
              <Accordion
                type="single"
                collapsible
                value={activeAccordion}
                onValueChange={setActiveAccordion}
              >
                {offerItems?.map((item, idx) => {
                  return (
                    <AccordionItem
                      key={item.sys.id}
                      value={`item-${idx}`}
                      className="relative pb-1 !mt-0 !mb-0 border-b border-neutral-200 lg:border-0"
                      itemScope
                      itemType="https://schema.org/Service"
                      itemProp="itemListElement"
                    >
                      <meta itemProp="position" content={String(idx + 1)} />
                      <AccordionTrigger
                        className={cn(
                          "p-4 pb-3 pl-0 text-lg text-semantic-text-primary leading-[160%] lg:text-gray-800 lg:text-xl lg:leading-[150%] cursor-pointer font-semibold hover:no-underline leading-[150%] flex justify-between will-change-colors transition-colors group-data-[state=open]:text-blue"
                        )}
                      >
                        <span itemProp="name">{item.fields.linkCategory}</span>
                      </AccordionTrigger>
                      <AccordionContent className="!pl-0 pt-3 pb-5 md:pr-4 md:pb-3  text-semantic-text-primary">
                        <p className="leading-[160%] lg:text-gray-600 lg:leading-[150%]" itemProp="description">
                          {item.fields.longText}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(IndustryOffer);
